import { QueryClient, useQuery } from 'react-query';

import { GqlRequestOptions } from '@common/api-request';
import { checkValidCatalogProductId } from '@common/utils';

import { getCatalogProductDetailPageOption } from '../graphql/select-option.2';
import { useGetProductIdInQueryParams } from '../hook/useGetProductIdInQueryParams';

const ERROR_INVALID_PARAMS_CATALOG_PRODUCT_ID = new Error('invalid params { catalog_product_id }');
const ERROR_FAILED_FETCH = new Error('failed to fetch');

interface OptionsParams {
  catalog_product_id: string;
  entry_source_type?: string;
}

const queryOptions = {
  refetchOnWindowFocus: false,
  cacheTime: Infinity,
  staleTime: Infinity,
};

export async function requestOptions(params: OptionsParams, ctx?: GqlRequestOptions) {
  try {
    if (!checkValidCatalogProductId(params.catalog_product_id)) {
      throw ERROR_INVALID_PARAMS_CATALOG_PRODUCT_ID;
    }

    const {
      data: { pdp_option_info },
    } = await getCatalogProductDetailPageOption(
      {
        catalog_product_id: params.catalog_product_id,
        input: params,
      },
      { ...ctx, show_alert: false },
    );
    if (!pdp_option_info || !pdp_option_info.catalog_product) {
      throw ERROR_FAILED_FETCH;
    }
    return pdp_option_info;
  } catch (e) {
    console.error(e);
  }
}

export const OPTIONS_QUERY_KEY = 'getCatalogProductOptionsInfo';

/**
 * 옵션 쿼리 설정 생성
 * @param params - catalog_product_id와 entry_source_type
 * @param options - GraphQL 요청 옵션
 * @returns React Query 옵션 객체
 */
export function getOptionsOption(params: OptionsParams, options?: GqlRequestOptions) {
  const queryKey = [OPTIONS_QUERY_KEY, params.catalog_product_id];

  return {
    queryKey,
    queryFn: async function () {
      return await requestOptions(params, options);
    },
    ...queryOptions,
    enabled: checkValidCatalogProductId(params.catalog_product_id),
  };
}

/**
 * 옵션 데이터를 미리 가져오기 (prefetch)
 * @param params - catalog_product_id와 entry_source_type
 * @param options - GraphQL 요청 옵션
 * @param query_client - React Query Client
 * @returns 프리페치된 데이터
 */
export async function prefetchOptions(params: OptionsParams, options: GqlRequestOptions, query_client: QueryClient) {
  const query_options = getOptionsOption(params, options);
  await query_client.prefetchQuery(query_options);
  return await query_client.getQueryData(query_options.queryKey);
}

/**
 * 상품 옵션 정보를 가져오는 훅
 * @param id - 상품 ID (없으면 URL의 query params에서 가져옴)
 * @param entry_source_type - 진입 경로 타입
 * @returns 옵션 데이터와 쿼리 상태
 */
export const useOptions = (id?: string, entry_source_type?: string) => {
  const productId = useGetProductIdInQueryParams();
  const catalog_product_id = id ?? productId;
  const queryResponse = useQuery(getOptionsOption({ catalog_product_id, entry_source_type }));

  return {
    ...queryResponse,
    options: queryResponse.error || !queryResponse.data ? null : queryResponse.data,
  };
};
